<template>
  <div class="container">
    <div class="row" style="margin: 0">
      <div class="col-md-12 col-xs-12 main-block">
        <div class="form-signin-heading text-center signin-text" style="margin-top: 50px;" v-html="loginTitle">
        </div>
        <div class="row">
          <TitleLogo :url="url + 'login'"/>
        </div>
        <div class="form-signin">
          <slot name="before"/>
          <template v-if="isEmailAuthAcceptMethod">
            <accept-login-email :login="form.login"/>
          </template>
          <template v-else-if="isPhoneAuthAcceptMethod">
            <accept-login-phone :user-id="userId"/>
          </template>
          <template v-else>
            <div class="form-group">
              <label for="inputEmail" class="sr-only">Адрес эл.почты</label>
              <input
                type="text"
                id="inputEmail"
                class="form-control"
                placeholder="Введите номер телефона или эл. адрес"
                v-model="form.login"
                required
                autofocus
                @input="captchaRemove"
              />
            </div>
            <div class="form-group">
              <label for="inputPassword" class="sr-only">Пароль</label>
              <input
                type="password"
                id="inputPassword"
                class="form-control"
                name="password"
                placeholder="Введите пароль (не обязательно)"
                v-model="form.password"
                @input="captchaRemove"
              />
            </div>
            <div class="row form-signin-buttons">
              <div class="col-md-6">
                <input
                  class="btn btn-md btn-primary btn-block btn-primary-blue"
                  type="submit"
                  id="buttonPassword"
                  value="Войти по паролю"
                  :disabled="disabledButtons"
                  @click="login"
                />
              </div>
              <div class="col-md-6">
                <input
                  class="btn btn-md btn-primary btn-block btn-primary-blue mt-md-10"
                  type="submit"
                  id="buttonLink"
                  name="send_me"
                  value="Войти без пароля"
                  :disabled="disabledButtons"
                  @click="loginNoPassword"
                />
              </div>
            </div>
            <div class="bottom-info-block">
              <template>
                <component
                  :api-token="configuration.yacaptcha_client_token"
                  :errors="errors"
                  :is="component"
                  :contents="timeoutMessage"
                  @captcha-solved="captchaSolved"
                />
              </template>
            </div>
            <p class="text-center note-signin">
              Если вы тут впервые, введите адрес электронной почты и нажмите
              кнопку «Войти без пароля». Пароль можно установить в личном кабинете после
              входа.
            </p>
          </template>
          <slot name="after"/>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AcceptLoginEmail from './AcceptLoginEmail'
import AcceptLoginPhone from './AcceptLoginPhone.vue'
import { ApiClient } from '../../helpers/api-client/ApiClient'
import { AuthAcceptMethods } from '../../enums/AuthAcceptMethods'
import ErrorInfo from '@/common/components/auth/information/ErrorInfo.vue'
import Info from '@/common/components/auth/information/Info.vue'
import LoginFormErrors from '@/common/classes/LoginFormErrors'
import NoPasswordFormErrors from '@/common/classes/NoPasswordFormErrors'
import { ResponseStatuses } from '../../enums/ResponseStatuses'
import TitleLogo from '../TitleLogo'
import YandexCaptcha from '@/common/components/YandexCaptcha'

export default {
  name: 'LoginForm',

  components: {
    AcceptLoginEmail,
    AcceptLoginPhone,
    ErrorInfo,
    Info,
    TitleLogo,
    YandexCaptcha
  },

  data: () => ({
    authAcceptMethod: AuthAcceptMethods.Default,
    disabledButtons: false,
    errors: [],
    userId: null,
    form: {
      captcha: {
        token: null,
        resolve: null,
        type: 'yacaptcha'
      },
      login: null,
      password: null
    },
    loginTitle: process.env.VUE_APP_LOGIN_TITLE,
    onResolve: false,
    timeout: 0,
    timerId: null,
    url: process.env.BASE_URL
  }),

  computed: {
    component () {
      if (this.timeout > 0) {
        return 'info'
      }

      if (this.errors.length > 0) {
        return 'error-info'
      }

      if (this.onResolve instanceof Function) {
        return 'yandex-captcha'
      }

      return 'div'
    },

    error () {
      return this.errors.join('\n')
    },

    isDefaultAuthAcceptMethod () {
      return this.authAcceptMethod === AuthAcceptMethods.Default
    },

    isEmailAuthAcceptMethod () {
      return this.authAcceptMethod === AuthAcceptMethods.Email
    },

    isPhoneAuthAcceptMethod () {
      return this.authAcceptMethod === AuthAcceptMethods.Phone
    },

    timeoutMessage () {
      return 'Вы можете повторить запрос через ' + this.timeout + ' сек.'
    }
  },

  methods: {
    async login () {
      this.disabledButtons = true
      this.errors = []

      ApiClient.login(this.form)
        .then(async ({ data }) => {
          let errorsDto = new LoginFormErrors(data.errors)
          if (errorsDto.authRequirements.length > 0) {
            this.errors.push('Необходимо заполнить логин и пароль')
          }

          let captchaErrors = Object.values(errorsDto.captcha).filter(value => value !== null)
          if (errorsDto.captchaRequirements.length > 0 || captchaErrors.length > 0) {
            this.captchaReload(this.login)
          }

          this.errors.push(
            ...Object.values(errorsDto.auth),
            ...Object.values(errorsDto.user)
          )
          this.errors = this.errors.filter(value => value !== null)

          if (data.status === ResponseStatuses.Success) {
            await this.$store.dispatch('authorize', data.data)
            await ApiClient.backendLogin()

            let rPath = await window.cookieStore.get('redirectPath') ?? null
            const redirectPath = rPath?.value || '/';
            await window.cookieStore.delete('redirectPath')

            if (redirectPath === '/' && (data.data.has_active_application === true || ['sb', 'subdivision', 'agent'].includes(data.data.user_role))) {
              window.location.replace(process.env.BASE_URL + 'application/list')
            }

            if (redirectPath.startsWith('http')) {
              window.location.href = decodeURIComponent(redirectPath);
            }

            await this.$router.push(redirectPath);
          }
        })
        .finally(() => {
          this.disabledButtons = false
        })
    },

    async loginNoPassword () {
      this.disabledButtons = true
      this.errors = []

      ApiClient.loginNoPassword(this.form)
        .then(async ({ data }) => {
          let errorsDto = new NoPasswordFormErrors(data.errors)
          this.errors.push(
            ...Object.values(errorsDto.auth),
            ...Object.values(errorsDto.user),
            errorsDto.user_access.attempts
          )
          if (errorsDto.authRequirements.length > 0) {
            this.errors.push('Необходимо заполнить «Логин»')
          }
          this.errors = this.errors.filter(value => value !== null)

          let captchaErrors = Object.values(errorsDto.captcha).filter(value => value !== null)
          if (errorsDto.captchaRequirements.length > 0 || captchaErrors.length > 0) {
            this.captchaReload(this.loginNoPassword)
          }

          this.restartCountdown(errorsDto.user_access.date_generation ?? 0)

          if (data.status === ResponseStatuses.Success) {
            this.authAcceptMethod = data.accept_method
            this.userId = data.data.user_id

            let rPath = await window.cookieStore.get('redirectPath') ?? null
            const redirectPath = rPath?.value || '/';
            await window.cookieStore.delete('redirectPath')

            if (redirectPath.startsWith('http')) {
              window.location.href = decodeURIComponent(redirectPath);
            }

            await this.$router.push(redirectPath);
          }
        })
        .finally(() => {
          this.disabledButtons = false
        })
    },

    restartCountdown (timeout) {
      clearInterval(this.timerId)
      this.timeout = timeout
      this.timerId = setInterval(
        () => {
          this.timeout--
          if (this.timeout < 0) {
            clearInterval(this.timerId)
            this.timerId = null
          }
        },
        1000
      );
    },

    captchaSolved (token) {
      let onResolve = this.onResolve
      this.onResolve = false
      this.form.captcha.token = token
      if (onResolve instanceof Function) {
        onResolve()
      }
    },

    captchaReload (resolve = () => {}) {
      this.captchaRemove()
      this.captchaRequest(resolve)
    },

    captchaRemove () {
      this.onResolve = false
      this.form.captcha.token = null
    },

    captchaRequest (resolve = () => {}) {
      this.onResolve = resolve
      this.form.captcha.token = null
    }
  }
}
</script>
